// Retirement on the Plan Order Form: an AGE or a DATE, one answer per person.
//
// Advisors say retirement as an age ("retire at 65"), and the form only took a
// date, so the question was answered by working a birthday forward in their
// head. Kyle Farmer asked for it on 2026-08-29 and again on 2026-09-14, with
// Blaine Thiederman seconding it. The Salesforce fields behind it,
// Client_Retirement_Date__c and Spouse_Retirement_Date__c, are Text(75), nothing
// downstream parses them as a date, and the eMoney push never reads the Plan
// Order Form, so the form can take either answer with no Salesforce change.
//
// What planning receives stays a date wherever one can be built honestly. The
// July fix for Marcus Gonzalez ("forced to put a date") settled that the Plan
// Order carries a real date made from date of birth plus age, and every recent
// record holds one. Only when there is no date of birth to build from does the
// age go across as text, "Age 67", which is still exactly what the advisor said
// and still reads plainly to a planner.
//
// This module is the ONE definition of all of that. The screen's prefill and
// the send payload used to decide it separately, and they had drifted: a PQ
// holding only a retirement age showed a derived date on screen, passed Submit,
// and sent null.

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;
const AGE_TEXT = /^\s*age\s+(\d{1,3}(?:\.\d+)?)\s*$/i;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Trimmed text, with the N/A placeholder the rest of the Plan Order scrubs
// treated as blank here too.
function clean(v) {
  const t = String(v ?? '').trim();
  return t === 'N/A' || t === 'n/a' ? '' : t;
}

// A usable age: a number above 0 and no more than 120, in its shortest form so
// "67.0" and "67" are the same answer. Anything else is ''.
export function normalizeAge(age) {
  const t = clean(age);
  if (t === '') return '';
  const n = Number(t);
  if (!Number.isFinite(n) || n <= 0 || n > 120) return '';
  return String(n);
}

// Date of birth plus age, as YYYY-MM-DD. A fractional age carries into months,
// so 66.5 lands six months after the 66th birthday instead of being rounded up
// to 67, which matters because Social Security states full retirement ages
// exactly that way. A day that does not exist in the target month (29 February,
// or the 31st) clamps to the last day of that month rather than producing an
// impossible date.
export function retirementDateFromAge(dob, age) {
  const m = ISO_DATE.exec(clean(dob));
  const a = normalizeAge(age);
  if (!m || a === '') return '';
  const monthIndex = (Number(m[2]) - 1) + Math.round(Number(a) * 12);
  const year = Number(m[1]) + Math.floor(monthIndex / 12);
  const month = ((monthIndex % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const day = Math.min(Number(m[3]), lastDay);
  const pad = x => String(x).padStart(2, '0');
  return `${String(year).padStart(4, '0')}-${pad(month + 1)}-${pad(day)}`;
}

// "Jun 2031" for the hint under the age box, or '' for anything not a date.
export function monthYear(iso) {
  const m = ISO_DATE.exec(clean(iso));
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : '';
}

// "67" -> "Age 67", and back again.
export function ageText(age) {
  const a = normalizeAge(age);
  return a === '' ? '' : `Age ${a}`;
}

export function ageFromText(v) {
  const m = AGE_TEXT.exec(String(v ?? ''));
  return m ? normalizeAge(m[1]) : '';
}

// A stored Salesforce value split back into the form's two boxes. "Age 65"
// fills the age box; anything else, including a legacy value like "2030", stays
// in the date box exactly as it arrived.
export function splitRetirement(v) {
  const age = ageFromText(v);
  return age ? { date: '', age } : { date: clean(v), age: '' };
}

// The questionnaire's own answer, as the advisor gave it there. A PQ retirement
// DATE fills the date box. A PQ retirement AGE, which the PQ only takes when no
// date is set, fills the age box. It used to be converted to a date on the way
// in, which hid that the advisor had answered with an age.
export function retirementPrefill(pqPerson) {
  const date = clean(pqPerson?.retirementDate);
  if (date) return { date, age: '' };
  return { date: '', age: normalizeAge(pqPerson?.retirementAge) };
}

// What one person's two boxes show. The stored answers win AS A PAIR: if the
// advisor has put anything in either box, that is the answer, and the
// questionnaire only shows through when both are empty. Resolving them together
// is what lets typing an age clear the date without the questionnaire's date
// reappearing in the box that was just emptied. Emptying both deliberately
// brings the questionnaire's answer back, and that is also what gets sent, so
// the screen and the payload never disagree.
export function resolveRetirement({ date, age, pq }) {
  const d = clean(date);
  const a = clean(age);
  if (d || a) return { date: d, age: d ? '' : a };
  return retirementPrefill(pq);
}

// The single value Salesforce receives for one person, or null when there is no
// answer anywhere.
export function retirementForSalesforce({ date, age, pq, dob }) {
  const r = resolveRetirement({ date, age, pq });
  if (r.date) return r.date;
  const a = normalizeAge(r.age);
  if (a === '') return null;
  return retirementDateFromAge(dob, a) || ageText(a);
}
